import React, { FC } from "react";
import { Link } from "react-router-dom";

import { existsAndNotEmpty } from "stentor-utils";
import { IconButton, TitleHeader, Tooltip, Button, PageAlert } from "@xapp/components";

import { IntegrationType } from "generated/graphql";

import { IntegrationsListDialog } from "./IntegrationsListDialog";
import { IntegrationList, IntegrationListProps } from "./IntegrationList";
import { IntegrationConnectionProps } from "./BasicIntegrationCard";

import style from "./style.scss";

export interface IntegrationViewProps extends IntegrationListProps {
    /**
     * Add Integration dialog is active
     */
    active?: boolean;
    /**
     * User has requested to add an integration. (SHOW THE DIALOG)
     */
    onAdd?(): void;
    /**
     * Dialog cancel
     */
    onCancel?(): void;
    /**
     * User is attempting to connect an integration
     *
     * This is used for the non-OAUTH integrations: ServiceTitan, Surefire, Housecall Pro
     */
    onConnect?(props: IntegrationConnectionProps): void;
    /**
     * User requested to disconnect the integration.
     */
    onDisconnect?(type: IntegrationType): void;
}

export const IntegrationsView: FC<IntegrationViewProps> = (props: IntegrationViewProps) => {

    const {
        active,
        app,
        data,
        limitIntegrations,
        omitIntegrations,
        onAdd,
        onboardingMode,
        onCancel,
        onSelected,
        selected,
        supportsIntegrations,
    } = props;

    const selectionMode = existsAndNotEmpty(selected);

    if (selectionMode) {
        // make sure we have a callback
        if (typeof onSelected !== "function") {
            throw new Error("onSelected must be a function when selected array is provided");
        }
    }

    // What does linked and active mean?
    // isLinked - User has set up a link to jobber
    // isActive - The link works

    const isLACRMLinked: boolean = data?.lacrm?.isLinked;
    const isJobberLinked: boolean = data?.jobber?.isLinked;
    const isServiceTitanLinked: boolean = data?.servicetitan?.isLinked;
    const isHousecallProLinked: boolean = data?.housecallpro?.isLinked;
    const isFieldPulseLinked: boolean = data?.fieldpulse?.isLinked;
    const isSurefireLocalLinked: boolean = data?.surefire?.isLinked;

    let availableIntegrations: string[] = existsAndNotEmpty(limitIntegrations) ? limitIntegrations : [
        "lacrm",
        "jobber",
        "surefire",
        "servicetitan",
        "housecallpro",
        "fieldpulse",
        "surefire",
        "zapier"
    ];

    if (existsAndNotEmpty(omitIntegrations)) {
        // Create a set for faster lookup
        const omitSet = new Set(omitIntegrations);

        // Filter out the omitted integrations
        availableIntegrations = availableIntegrations.filter(integration => {
            if (omitSet.has(integration)) {
                return false;
            }
            return true;
        });
    }

    const linkedIntegrations: string[] = [];

    if (isLACRMLinked) {
        linkedIntegrations.push('lacrm');
    }

    if (isJobberLinked) {
        linkedIntegrations.push("jobber");
    }

    if (isServiceTitanLinked) {
        linkedIntegrations.push("servicetitan");
    }

    if (isHousecallProLinked) {
        linkedIntegrations.push("housecallpro");
    }

    if (isFieldPulseLinked) {
        linkedIntegrations.push("fieldpulse");
    }

    if (isSurefireLocalLinked) {
        linkedIntegrations.push("surefire");
    }

    const hasLinkedIntegration: boolean = existsAndNotEmpty(linkedIntegrations);

    return (
        <>
            <TitleHeader value={"Integrations"}>
                <Tooltip content={supportsIntegrations ? "Add" : "Not Supported"}>
                    <IconButton iconName="library_add" onClick={onAdd} color="primary" disabled={!supportsIntegrations} />
                </Tooltip>
            </TitleHeader>
            {!supportsIntegrations && !onboardingMode ? (
                <div>
                    <PageAlert background="orange">
                        <p>Integrations are not supported on your current plan.  Please upgrade to one of our paid tiers to integrate with 3rd party service providers.</p>
                        <div style={{ display: "flex", flexDirection: "column", rowGap: "10px", alignItems: "center" }}>
                            <Link to={`/${app.organizationId}/${app.appId}/upgrade`}>
                                <Button variation="primary" label="Upgrade Plan" />
                            </Link>
                        </div>
                    </PageAlert>
                </div>
            ) : <></>}

            {!hasLinkedIntegration && typeof onAdd === "function" ? (
                <div className={style.noIntegrationContainer}>
                    <h3>You don't have any integrations setup yet.</h3>
                    <p> Click one below to get started. </p>
                </div>
            ) : <></>}

            <IntegrationList
                {...props}
                app={app}
                data={data}
                onboardingMode={!hasLinkedIntegration}
                onConnect={props.onConnect}
                disabled={!supportsIntegrations}
                onDisconnect={!hasLinkedIntegration ? null : props.onDisconnect}
            />

            <IntegrationsListDialog
                app={app}
                active={active}
                disabled={!supportsIntegrations}
                integrations={availableIntegrations}
                onCancel={onCancel}
            />
        </>
    );
}